// Modules
import { connect } from 'react-redux';
import Promise from 'bluebird';
import PropTypes from 'prop-types';
import React from 'react';
import _ from 'lodash';
import moment from 'moment';

import { Element } from 'components/forms/form-elements';
import { Page, PageHeader, PageContent, ActionSection } from 'components/page';
import { PrimaryButton, SecondaryButton, WarningButton } from 'components/widgets/buttons/buttons';
import { Row } from 'components/forms/form-structure';
import { alerts } from 'modules/alerts/actions';
import { didQueryUpdate } from 'utils/filter-utils';
import { actions as modalActions } from 'modules/modals/actions';
import CheckableActions from 'components/content-containers/tables/checkable-actions';
import Filters from 'modules/filters/filters';
import Invoice from 'services/data/invoice';
import InvoicesTable from 'components/models/invoice/invoices-table';
import Pager from 'components/widgets/pager/pager';

import * as actions from './store/actions';


// Component
class Invoices extends React.Component {
  static propTypes = {
    invoices: PropTypes.array,
    fetching: PropTypes.bool,
    sendingEmails: PropTypes.bool,
    markingMailed: PropTypes.bool,
    markingEmailed: PropTypes.bool,
    markingAchCompleted: PropTypes.bool,
    errorSendingEmails: PropTypes.bool,
    count: PropTypes.number,
    query: PropTypes.object
  };

  constructor() {
    super();

    this.emailInvoices = this.emailInvoices.bind(this);
    this.markMailed = this.markMailed.bind(this);
    this.markEmailed = this.markEmailed.bind(this);
    this.confirmMarkEmailed = this.confirmMarkEmailed.bind(this);
    this.markAchCompleted = this.markAchCompleted.bind(this);
    this.search = this.search.bind(this);
    this.checkBalances = this.checkBalances.bind(this);
    this.update = invoices => actions.set({ invoices });

    this.filterConfig = {
      basic: ['company'],
      advanced: [
        ['company', 'team', 'invoiceStatus', 'achStatus'],
        ['invoiceDate', 'dueDate', 'invoiceNumber'],
        ['paymentMethod', 'invoiceMethod', 'paymentStatus']
      ],
      defaults: {
        invoiceStatus: ['Delivered', 'Not Delivered'],
        achStatus: ['ACH Processed', 'ACH Not Processed'],
        paymentMethod: ['ACH', 'Check'],
        invoiceMethod: ['Mail', 'Email'],
        paymentStatus: ['Paid', 'Unpaid']
      }
    };

    this.state = {};
  }

  componentWillReceiveProps(nextProps) {
    if (didQueryUpdate(nextProps, this.props)) this.search(nextProps.query);
  }

  search(query) {
    actions.set({ fetching: true });
    const fullQuery = Invoice.formatQuery(query || this.props.query);

    return Promise.props({
      invoices: Invoice.find(fullQuery),
      count: Invoice.count({ where: fullQuery.where })
    })
      .then((response) => {
        actions.set({ invoices: response.invoices, fetching: false, count: response.count });
      })
      .catch(alerts.catchError);
  }

  markMailed() {
    actions.set({ markingMailed: true });

    return this.updateInvoices({ mailedDate: moment().toDate() })
      .then(() => {
        actions.set({ markingMailed: false });
      });
  }

  confirmMarkEmailed() {
    const { invoices } = this.props;

    const checked = _(invoices)
      .filter('checked')
      .value();

    modalActions.showDeleteModal({
      onConfirm: this.markEmailed,
      title: 'Are you sure you want to mark these emailed?',
      body: 'This will mark these invoices as emailed WITHOUT ACTUALLY EMAILING THEM! Make sure this is something you really want to do.',
      confirmationString: `MARK EMAILED ${checked.length}`,
      confirmText: "Mark Emailed"
    });
  }

  markEmailed() {
    actions.set({ markingEmailed: true });

    return this.updateInvoices({ emailedDate: moment().toDate() })
      .then(() => {
        actions.set({ markingEmailed: false });
      });
  }

  markAchCompleted() {
    actions.set({ markingAchCompleted: true });

    return this.updateInvoices({ achProcessedDate: moment().toDate() })
      .then(() => {
        actions.set({ markingAchCompleted: false });
      });
  }

  updateInvoices(changes) {
    const { invoices, query } = this.props;

    const updates = _(invoices)
      .filter('checked')
      .map(invoice => _.assign({ id: invoice.id }, changes))
      .value();

    return Invoice.updateMany(updates)
      .then(() => this.search(query));
  }

  checkBalances() {
    this.setState({ checkingBalances: true });

    return Invoice.checkBalances()
      .then(() => {
        this.setState({ checkingBalances: false });
        alerts.success({ title: 'Balance check complete!' });
        this.search();
      });
  }

  /**
   * Emails invoices and then refreshes the page data
   */
  emailInvoices() {
    actions.set({ sendingEmails: true });
    const { invoices, query } = this.props;
    const invoiceIds = _.compact(_.map(invoices, (invoice) => (invoice.checked ? invoice.id : undefined)));

    return Invoice.emailInvoices(invoiceIds)
      .then(() => {
        actions.set({ sendingEmails: false });
        return this.search(query);
      })
      .catch((err) => {
        actions.set({ sendingEmails: false, errorSendingEmails: true });
        alerts.catchError(err);
      });
  }

  render() {
    const { invoices = [], fetching, count = 0, sendingEmails, markingMailed, markingEmailed, markingAchCompleted, errorSendingEmails } = this.props;
    const anyChecked = _.some(invoices, 'checked');

    _.forEach(invoices, (invoice) => {
      const pending = invoice.xeroSyncStatus !== 'Completed';
      const method = _.get(invoice, 'company.invoiceMethod');
      const payment = _.get(invoice, 'company.paymentMethod');
      const missingFields = !method || !payment;
      const noMail = method === 'Mail' && !_.get(invoice, 'company.billingAddress.id');
      const noEmail = method === 'Email' && _.isEmpty(invoice.company.billingEmails);
      const noPONumber = !invoice.purchaseOrderNumber && invoice.company.purchaseOrderNumberRequired;

      // Set checkboxDisabled explicitly so checkItems util functions work. Invoices cannot be sent if
      // they don't have a pdf or if they have no billing emails and aren't set to be sent via mail
      if (pending) invoice.checkboxDisabled = 'Pending xero sync and pdf generation.';
      else if (missingFields) invoice.checkboxDisabled = 'This company needs an invoice method or payment method to be set.';
      else if (noEmail) invoice.checkboxDisabled = 'This company is set to receive emailed invoices but doesn\'t have any billing emails set.';
      else if (noMail) invoice.checkboxDisabled = 'This company is set to receive mailed invoices but doesn\'t have a billing address set.';
      else if (noPONumber) invoice.checkboxDisabled = 'This company requires a purchase order number for invoices.';
    });

    const { checkingBalances } = this.state;

    const headerActions = (
      <div className="header-actions">
        <SecondaryButton onClick={this.checkBalances} showLoading={checkingBalances}>Check Balances</SecondaryButton>
      </div>
    );

    return (
      <Page>
        <PageHeader title="Invoices" actions={headerActions}>
          <ActionSection>
            <Filters className={anyChecked ? 'hidden' : ''} config={this.filterConfig} />
            {anyChecked &&
              <CheckableActions list={invoices} update={this.update}>
                <Row className="no-divider">
                  <Element>
                    <PrimaryButton onClick={this.emailInvoices} showLoading={sendingEmails} showComplete={!errorSendingEmails}>Email Invoices</PrimaryButton>
                    <PrimaryButton onClick={this.markMailed} showLoading={markingMailed} showComplete>Mark Invoices Mailed</PrimaryButton>
                    <PrimaryButton onClick={this.markAchCompleted} showLoading={markingAchCompleted} showComplete>Mark ACH Processed</PrimaryButton>
                    <WarningButton onClick={this.confirmMarkEmailed} showLoading={markingEmailed} showComplete>Mark Invoices Emailed</WarningButton>
                  </Element>
                </Row>
              </CheckableActions>
            }
          </ActionSection>
        </PageHeader>
        <PageContent
          fetching={fetching}
          emptyText="No invoices, adjust your filters!"
          emptyTextIcon="svg/filter.svg"
          showEmptyText={!fetching && _.isEmpty(invoices)}
          className="invoices"
        >
          <InvoicesTable invoices={invoices} update={this.update} />
          <Pager count={count} pageSize={Invoice.defaultQuery.limit} />
        </PageContent>
      </Page>
    );
  }
}

// Exports
export default connect((state) => ({
  invoices: state.invoices.invoices,
  fetching: state.invoices.fetching,
  count: state.invoices.count,
  sendingEmails: state.invoices.sendingEmails,
  markingMailed: state.invoices.markingMailed,
  markingAchCompleted: state.invoices.markAchCompleted,
  errorSendingEmails: state.invoices.errorSendingEmails,
  query: state.location.query
}))(Invoices);
